import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  BookOpen,
  ShoppingCart,
  IndianRupee,
  Wallet,
  Download,
  ArrowLeft,
  Edit2,
  Trash2,
  MoreVertical,
} from "lucide-react";
import ThemedTable from "../../components/ThemedTable";
import AdjustPaymentModal from "./AdjustPaymentModal";
import { apiClient } from "../../apiclient/apiclient";
import Loader from "../../components/Loader";
import { toast } from "react-toastify";

/* ================= HELPERS ================= */
const formatAmount = (amt) => (amt ? `₹ ${Number(amt).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-");

const formatBalance = (bal) =>
  bal > 0
    ? `₹ ${Number(bal).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Debit`
    : bal < 0
      ? `₹ ${Math.abs(Number(bal)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Credit`
      : "₹ 0.00";

// Format opening balance from client object
const formatOpeningBalance = (openingBalance, openingBalanceType) => {
  if (openingBalance == null) return "—";
  const abs = Math.abs(Number(openingBalance));
  if (abs === 0) return "₹ 0.00";
  return `₹ ${abs.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${openingBalanceType === "credit" ? "Credit" : "Debit"}`;
};

const ClientLedger = () => {
  /* ================= ROUTE PARAM ================= */
  const { id: clientId } = useParams();
  const navigate = useNavigate();

  // User Role & Permissions Check
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "superAdmin";

  /* ================= STATE ================= */
  const [ledgerData, setLedgerData] = useState([]);
  const [client, setClient] = useState(null);

  const [summary, setSummary] = useState({
    openingBalance: 0,
    totalDebit: 0,
    totalCredit: 0,
    currentBalance: 0,
  });
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalOrderAmount: 0,
  });

  const [loading, setLoading] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [activeDropdownId, setActiveDropdownId] = useState(null);
  const [search, setSearch] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [type, setType] = useState("all");

  const handleEditClick = (row) => {
    setEditData(row);
    setAdjustOpen(true);
  };

  const handleDeleteClick = async (row) => {
    if (
      window.confirm(
        `Are you sure you want to permanently delete this manual payment of ₹${row.credit}? This will automatically recalculate all subsequent running balances.`
      )
    ) {
      try {
        await apiClient.delete(`/clients/ledger/${row._id}`);
        toast.success("Payment deleted successfully");
        fetchLedger();
      } catch (err) {
        console.error("Delete payment error:", err);
        toast.error(err.response?.data?.message || "Failed to delete payment");
      }
    }
  };

  /* ================= FETCH LEDGER ================= */
  const fetchLedger = useCallback(async () => {
    if (!clientId) return;

    try {
      setLoading(true);

      const res = await apiClient.get(`/clients/${clientId}/client-ledger`);

      setLedgerData(
        (res.data.ledger || []).filter((row) => row.type !== "opening"),
      );
      setSummary({
        openingBalance: res.data.openingBalance || 0,
        totalDebit: res.data.totalDebit || 0,
        totalCredit: res.data.totalCredit || 0,
        currentBalance: res.data.currentBalance || 0,
      });
    } catch (error) {
      console.error("ledger fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const handleDownloadReport = async () => {
    if (!filteredLedger.length) {
      alert("No data to download");
      return;
    }

    try {
      const res = await apiClient.get(
        `/clients/${clientId}/client-ledger-excel`,
        {
          responseType: "blob", // 👈 VERY IMPORTANT
        },
      );

      // Create file from blob
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      // Auto download (NO new tab)
      const a = document.createElement("a");
      a.href = url;
      a.download = `Client-Ledger-${client?.name || "Report"}.xlsx`;
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed", error);
      alert("Failed to download report");
    }
  };

  const getInitials = (name = "") => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  useEffect(() => {
    const orders = ledgerData.filter((row) => row.type === "order");

    const totalOrders = orders.length;

    const totalOrderAmount = orders.reduce((sum, o) => sum + (o.debit || 0), 0);

    setStats({
      totalOrders,
      totalOrderAmount,
    });
  }, [ledgerData]);

  /* ================= EFFECT ================= */
  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (activeDropdownId && !e.target.closest(".dropdown-container")) {
        setActiveDropdownId(null);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [activeDropdownId]);

  /* ================= FILTERED LEDGER ================= */
  const filteredLedger = useMemo(() => {
    return ledgerData.filter((row) => {
      // Hide any row where:
      // - type is 'opening'
      // - type is 'adjustment' and description contains 'opening balance updated'
      // - description contains 'opening balance' (for any type)
      if (
        row.type === "opening" ||
        (row.type === "adjustment" &&
          row.description &&
          /opening balance updated/i.test(row.description)) ||
        (row.description &&
          /opening balance(?! updated)/i.test(row.description))
      ) {
        return false;
      }
      const rowDate = new Date(row.createdAt);

      if (fromDate && rowDate < new Date(fromDate)) return false;
      if (toDate && rowDate > new Date(toDate + "T23:59:59")) return false;

      if (type === "debit" && row.debit <= 0) return false;
      if (type === "credit" && row.credit <= 0) return false;

      if (
        search &&
        !row.description?.toLowerCase().includes(search.toLowerCase())
      )
        return false;

      return true;
    });
  }, [ledgerData, fromDate, toDate, type, search]);

  const fetchClient = useCallback(async () => {
    if (!clientId) return;
    const res = await apiClient.get(`/clients/${clientId}`);
    setClient(res.data.client);
  }, [clientId]);

  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  return (
    <div className="flex flex-col ">
      {/* ================= HEADER ================= */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 border border-slate-300 rounded-md hover:bg-white transition-colors bg-slate-100 text-slate-700"
            title="Go Back"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Client Ledger</h1>
            <p className="text-sm text-slate-500 mt-1">
              Complete transaction history with running balance
            </p>
          </div>
        </div>

        <button
          onClick={() => { setEditData(null); setAdjustOpen(true); }}
          className="px-4 py-2 me-4 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors text-sm font-medium w-auto"
        >
          Record Payment
        </button>
      </div>

      {/* ================= SUMMARY CARDS ================= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
        {/* CLIENT INFO */}
        <div className="bg-white px-4 py-6 border-b border-e text-center flex flex-col justify-center items-center">
          <div className="text-lg xl:text-xl font-bold leading-tight truncate w-full text-[#172554]">
            {client?.name || "—"}
          </div>
          <div className="mt-1 text-[11px] text-[#172554]/70 font-bold uppercase tracking-wide truncate w-full">
            {client?.companyName || client?.phone || "Client Info"}
          </div>
        </div>

        <div className="bg-white px-4 py-6 border-b border-e transition text-center flex flex-col justify-center items-center">
          <div className="text-lg xl:text-xl font-bold leading-tight truncate w-full text-[#172554]">
            {formatOpeningBalance(
              client?.openingBalance,
              client?.openingBalanceType,
            )}
          </div>
          <div className="mt-1 text-[11px] text-[#172554]/70 font-bold uppercase tracking-wide">
            Opening Balance
          </div>
        </div>

        <div className="bg-white px-4 py-6 border-e border-b text-center flex flex-col justify-center items-center">
          <div className="text-lg xl:text-xl font-bold leading-tight truncate w-full text-[#172554]">
            {stats.totalOrders}
          </div>
          <div className="mt-1 text-[11px] text-[#172554]/70 font-bold uppercase tracking-wide">
            Total Orders
          </div>
        </div>

        <div className="bg-white px-4 py-6 border-e border-b text-center flex flex-col justify-center items-center">
          <div className="text-lg xl:text-xl font-bold leading-tight truncate w-full text-[#172554]">
            ₹ {stats.totalOrderAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="mt-1 text-[11px] text-[#172554]/70 font-bold uppercase tracking-wide">
            Total Amount
          </div>
        </div>

        <div className="bg-white px-4 py-6 border-e border-b text-center flex flex-col justify-center items-center">
          <div className="text-lg xl:text-xl font-bold leading-tight truncate w-full text-[#172554]">
            {formatBalance(summary.currentBalance)}
          </div>
          <div className="mt-1 text-[11px] text-[#172554]/70 font-bold uppercase tracking-wide">
            Current Balance
          </div>
        </div>
      </div>

      {/* ================= FILTER BAR (LIGHT SECTION) ================= */}
      <div className="border-t bg-[#EFF6FF] p-3 mb-0">
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full sm:w-auto"
          />

          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full sm:w-auto"
          />

          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full sm:w-auto min-w-[120px]"
          >
            <option value="all">All</option>
            <option value="debit">Debit</option>
            <option value="credit">Credit</option>
          </select>

          <div className="relative w-full sm:flex-1">
            <input
              type="text"
              placeholder="Search description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-3 py-2 w-full text-sm bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition"
            />
          </div>

          <button
            onClick={handleDownloadReport}
            className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors flex items-center justify-center w-full sm:w-auto"
            title="Download Report"
          >
            <Download size={20} />
          </button>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <div className="border border-slate-200 rounded-lg bg-white">
        <div className="overflow-x-auto overflow-y-scroll pb-20">
          <ThemedTable className="text-left border-collapse w-full min-w-[880px]">
            <thead>
              <tr>
                <th className="w-2/12 text-left pl-6">Date</th>
                <th className="w-4/12 text-left">Description</th>
                <th className="w-2/12 text-right">Debit</th>
                <th className="w-2/12 text-right">Credit</th>
                <th className="w-2/12 text-right">Balance</th>
                {isAdmin && <th className="w-1/12 text-center">Actions</th>}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={isAdmin ? 6 : 5} className="py-10 text-center">
                    <Loader text="Loading clients Ledger..." />
                  </td>
                </tr>
              ) : (
                <>
                  <tr className="bg-slate-50 hover:bg-slate-50">
                    <td className="whitespace-nowrap text-[13px] text-slate-400 font-medium pl-6 !py-1.5 text-center">-</td>
                    <td className="text-[13px] font-bold text-slate-700 !py-1.5">Opening Balance</td>
                    <td className="text-right text-[13px] text-emerald-600 font-semibold !py-1.5">
                      {client?.openingBalanceType === "debit" && client?.openingBalance ? formatAmount(client.openingBalance) : "-"}
                    </td>
                    <td className="text-right text-[13px] text-rose-600 font-semibold !py-1.5">
                      {client?.openingBalanceType === "credit" && client?.openingBalance ? formatAmount(client.openingBalance) : "-"}
                    </td>
                    <td className="text-right text-[13px] font-bold text-slate-800 whitespace-nowrap !py-1.5">
                      {formatOpeningBalance(client?.openingBalance, client?.openingBalanceType)}
                    </td>
                    {isAdmin && <td className="text-center !py-1.5 text-slate-400 text-xs font-semibold">—</td>}
                  </tr>

                  {filteredLedger.length > 0 ? (
                    filteredLedger.map((row) => {
                      const isManualPayment = row.type === "payment" && !row.referenceId;
                      return (
                        <tr key={row._id} className="hover:bg-slate-50/50">
                          <td className="whitespace-nowrap !py-1.5 pl-6">
                            <span className="font-semibold text-slate-700 text-[13px]">
                              {new Date(row.createdAt).toLocaleDateString("en-IN", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })}
                            </span>
                            <span className="text-[11px] text-slate-400 font-medium ml-2">
                              {new Date(row.createdAt).toLocaleTimeString("en-IN", {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })}
                            </span>
                          </td>
                          <td className="text-[13px] font-medium text-slate-800 whitespace-nowrap !py-1.5">
                            {row.description}
                          </td>
                          <td className="text-right text-[13px] text-emerald-600 font-semibold !py-1.5">
                            {row.debit ? formatAmount(row.debit) : "-"}
                          </td>
                          <td className="text-right text-[13px] text-rose-600 font-semibold !py-1.5">
                            {row.credit ? formatAmount(row.credit) : "-"}
                          </td>
                          <td className="text-right text-[13px] font-bold text-slate-800 whitespace-nowrap !py-1.5">
                            {formatBalance(row.balanceAfter)}
                          </td>
                          {isAdmin && (
                            <td className="text-center !py-1.5 relative">
                              {isManualPayment ? (
                                <div className="dropdown-container relative inline-block">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveDropdownId(activeDropdownId === row._id ? null : row._id);
                                    }}
                                    className="p-1 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition active:scale-95 flex items-center justify-center mx-auto"
                                    title="More Actions"
                                  >
                                    <MoreVertical size={15} />
                                  </button>

                                  {activeDropdownId === row._id && (
                                    <div className="absolute right-0 mt-1 w-24 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                                      <button
                                        onClick={() => {
                                          setActiveDropdownId(null);
                                          handleEditClick(row);
                                        }}
                                        className="w-full text-left px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50 hover:text-blue-600 flex items-center gap-1.5 transition-colors"
                                      >
                                        <Edit2 size={12} className="text-blue-500" />
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => {
                                          setActiveDropdownId(null);
                                          handleDeleteClick(row);
                                        }}
                                        className="w-full text-left px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50 hover:text-red-600 flex items-center gap-1.5 transition-colors border-t border-slate-100"
                                      >
                                        <Trash2 size={12} className="text-red-500" />
                                        Delete
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-slate-400 text-xs font-semibold">—</span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={isAdmin ? 6 : 5} className="py-10 text-center text-slate-500">
                        No ledger entries found
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </ThemedTable>
        </div>
      </div>

      {/* ================= MODAL ================= */}
      <AdjustPaymentModal
        open={adjustOpen}
        onClose={() => { setAdjustOpen(false); setEditData(null); }}
        clientId={clientId}
        editData={editData}
        onSuccess={fetchLedger}
      />
    </div>
  );
};

export default ClientLedger;
